/**
 * Image compression utilities for optimizing uploads
 * Centralized presets for consistent image compression across the app
 */

export interface CompressImageOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  format?: 'webp' | 'jpeg' | 'png';
  backgroundColor?: string;
  preserveTransparency?: boolean;
}

/**
 * Centralized image presets for all upload contexts
 * Each preset is optimized for its specific use case
 */
export const IMAGE_PRESETS = {
  avatar: { maxWidth: 256, maxHeight: 256, quality: 0.85, format: 'webp' as const },
  forjaNotes: { maxWidth: 1000, maxHeight: 1000, quality: 0.65, format: 'webp' as const },
  forjaTasksCover: { maxWidth: 800, maxHeight: 600, quality: 0.80, format: 'webp' as const },
  marketplaceThumbnail: { maxWidth: 800, maxHeight: 600, quality: 0.85, format: 'webp' as const },
  siteLogo: { maxWidth: 400, maxHeight: 200, quality: 0.90, format: 'webp' as const, preserveTransparency: true },
  toolBackground: { maxWidth: 600, maxHeight: 400, quality: 0.80, format: 'webp' as const },
  partnerBanner: { maxWidth: 1200, maxHeight: 400, quality: 0.85, format: 'webp' as const },
  partnerLogo: { maxWidth: 400, maxHeight: 400, quality: 0.85, format: 'webp' as const },
  campaignThumbnail: { maxWidth: 600, maxHeight: 400, quality: 0.80, format: 'webp' as const },
  homeBannerDesktop: { maxWidth: 1550, maxHeight: 200, quality: 0.85, format: 'webp' as const },
  homeBannerMobile: { maxWidth: 960, maxHeight: 1200, quality: 0.90, format: 'webp' as const },
  notificationAttachment: { maxWidth: 800, maxHeight: 800, quality: 0.65, format: 'webp' as const },
  feedbackAttachment: { maxWidth: 1000, maxHeight: 1000, quality: 0.65, format: 'webp' as const },
  chatAttachment: { maxWidth: 1000, maxHeight: 1000, quality: 0.65, format: 'webp' as const, preserveTransparency: true },
  badgeIcon: { maxWidth: 64, maxHeight: 64, quality: 0.90, format: 'webp' as const, preserveTransparency: true },
  arsenalThumbnail: { maxWidth: 400, maxHeight: 400, quality: 0.85, format: 'webp' as const },
  paymentProof: { maxWidth: 1200, maxHeight: 1200, quality: 0.70, format: 'webp' as const },
  pixQrCode: { maxWidth: 800, maxHeight: 800, quality: 0.90, format: 'webp' as const, preserveTransparency: true },
} as const;

export type ImagePresetKey = keyof typeof IMAGE_PRESETS;

const defaultOptions: CompressImageOptions = {
  maxWidth: 400,
  maxHeight: 400,
  quality: 0.8,
  format: 'webp',
  backgroundColor: '#0a0b0d', // Match app dark background
  preserveTransparency: false,
};

/**
 * Compresses an image file by resizing and converting to specified format
 * @param file - The image file to compress
 * @param options - Compression options
 * @returns A promise that resolves to a compressed Blob
 */
export async function compressImage(
  file: File,
  options: CompressImageOptions = {}
): Promise<Blob> {
  const opts = { ...defaultOptions, ...options };

  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      reject(new Error('Could not get canvas context'));
      return;
    }

    img.onload = () => {
      // Calculate new dimensions maintaining aspect ratio
      let { width, height } = img;
      const maxW = opts.maxWidth!;
      const maxH = opts.maxHeight!;

      if (width > maxW || height > maxH) {
        const ratio = Math.min(maxW / width, maxH / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }

      // iOS Safari: canvas with any dimension > 4096 silently fails drawImage,
      // producing an empty/background-only canvas. Cap to a safe size.
      const IOS_SAFE_DIMENSION = 4096;
      if (width > IOS_SAFE_DIMENSION || height > IOS_SAFE_DIMENSION) {
        const ratio = Math.min(IOS_SAFE_DIMENSION / width, IOS_SAFE_DIMENSION / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }

      // Set canvas dimensions
      canvas.width = width;
      canvas.height = height;

      // Only fill background if not preserving transparency
      if (!opts.preserveTransparency) {
        ctx.fillStyle = opts.backgroundColor || '#0a0b0d';
        ctx.fillRect(0, 0, width, height);
      }
      try {
        ctx.drawImage(img, 0, 0, width, height);
      } catch (err) {
        reject(new Error('Não foi possível processar a imagem — tente enviar em outro formato.'));
        return;
      }

      // Anti-blank-image guard: sample pixels to detect when drawImage failed silently
      // and the canvas contains only the background color (or full transparency).
      try {
        const sampleCount = 16;
        const sampleData: number[][] = [];
        for (let i = 0; i < sampleCount; i++) {
          const x = Math.floor((i / sampleCount) * width);
          const y = Math.floor(((i * 7) % sampleCount) / sampleCount * height);
          const px = ctx.getImageData(x, y, 1, 1).data;
          sampleData.push([px[0], px[1], px[2], px[3]]);
        }
        const allTransparent = sampleData.every(p => p[3] === 0);
        const first = sampleData[0];
        const allSameColor = sampleData.every(p =>
          p[0] === first[0] && p[1] === first[1] && p[2] === first[2] && p[3] === first[3]
        );
        if (allTransparent || allSameColor) {
          reject(new Error('Não foi possível processar a imagem — tente enviar em outro formato.'));
          return;
        }
      } catch {
        // getImageData may fail on tainted canvases; fall through and trust drawImage.
      }

      // Convert to blob
      const mimeType = `image/${opts.format}`;
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Could not compress image'));
          }
        },
        mimeType,
        opts.quality
      );
    };

    img.onerror = () => {
      reject(new Error('Could not load image'));
    };

    // Load the image from file
    const reader = new FileReader();
    reader.onload = (e) => {
      img.src = e.target?.result as string;
    };
    reader.onerror = () => {
      reject(new Error('Could not read file'));
    };
    reader.readAsDataURL(file);
  });
}

/**
 * Gets the file extension for a given format
 */
export function getExtensionForFormat(format: 'webp' | 'jpeg' | 'png'): string {
  return format === 'jpeg' ? 'jpg' : format;
}

/**
 * Prepares an image for upload by compressing it according to the specified preset
 * @param file - The image file to prepare
 * @param preset - The preset key to use for compression
 * @returns A promise with the compressed blob, extension, and mime type
 */
export async function prepareImageForUpload(
  file: File,
  preset: ImagePresetKey
): Promise<{ blob: Blob; extension: string; mimeType: string }> {
  const options = IMAGE_PRESETS[preset];
  const blob = await compressImage(file, options);
  return { 
    blob, 
    extension: 'webp',
    mimeType: 'image/webp'
  };
}

export interface CropToExactOptions {
  width: number;
  height: number;
  quality?: number;
  format?: 'webp' | 'jpeg' | 'png';
  backgroundColor?: string;
}

/**
 * Crops and resizes an image to exact dimensions using "cover" strategy
 * This ensures the output is ALWAYS the specified size, cropping excess if needed
 * @param file - The image file to process
 * @param options - Target dimensions and quality
 * @returns A promise that resolves to a Blob with exact dimensions
 */
export async function cropToExact(
  file: File,
  options: CropToExactOptions
): Promise<Blob> {
  const { width, height, quality = 0.85, format = 'webp', backgroundColor = '#0a0b0d' } = options;

  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      reject(new Error('Could not get canvas context'));
      return;
    }

    img.onload = () => {
      // Set canvas to exact target dimensions
      canvas.width = width;
      canvas.height = height;

      // Calculate "cover" dimensions (fill the canvas, crop excess)
      const imgRatio = img.width / img.height;
      const canvasRatio = width / height;

      let srcX = 0;
      let srcY = 0;
      let srcW = img.width;
      let srcH = img.height;

      if (imgRatio > canvasRatio) {
        // Image is wider than canvas ratio - crop sides
        srcW = img.height * canvasRatio;
        srcX = (img.width - srcW) / 2;
      } else {
        // Image is taller than canvas ratio - crop top/bottom
        srcH = img.width / canvasRatio;
        srcY = (img.height - srcH) / 2;
      }

      // Fill with dark background first (matches app theme)
      ctx.fillStyle = backgroundColor;
      ctx.fillRect(0, 0, width, height);

      // Draw the cropped portion to fill the canvas
      ctx.drawImage(img, srcX, srcY, srcW, srcH, 0, 0, width, height);

      // Convert to blob
      const mimeType = `image/${format}`;
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Could not create image blob'));
          }
        },
        mimeType,
        quality
      );
    };

    img.onerror = () => {
      reject(new Error('Could not load image'));
    };

    // Load the image from file
    const reader = new FileReader();
    reader.onload = (e) => {
      img.src = e.target?.result as string;
    };
    reader.onerror = () => {
      reject(new Error('Could not read file'));
    };
    reader.readAsDataURL(file);
  });
}

/**
 * Prepares a cropped image for upload (for exact dimension requirements like partner logos/banners)
 * @param file - The image file to prepare
 * @param preset - The preset key to determine dimensions
 * @returns A promise with the cropped blob, extension, and mime type
 */
export async function prepareCroppedImageForUpload(
  file: File,
  preset: 'partnerLogo' | 'partnerBanner'
): Promise<{ blob: Blob; extension: string; mimeType: string }> {
  const presetConfig = IMAGE_PRESETS[preset];
  const blob = await cropToExact(file, {
    width: presetConfig.maxWidth,
    height: presetConfig.maxHeight,
    quality: presetConfig.quality,
    format: presetConfig.format,
  });
  return { 
    blob, 
    extension: 'webp',
    mimeType: 'image/webp'
  };
}
